import { Typography, Link } from '@mui/material';

import { useMemo, useCallback } from 'react';
import { useAtom, useAtomValue } from 'jotai';

import * as state from '@/components/Analysis/state';
import { GetStructureLocationsForInspectionDataLoaderQuery } from '@/__generated__/graphql';
import { DockPanel } from '@/components/shared/DockPanel';

import { mapLocationToCurrentImage } from '../../hooks/useImageFromLocationId';

import { Number3 } from '@abyss/3d-viewer';
import { ViewpointThumbnail } from './ViewpointThumbnail';
import { ImageThumbailContainer, ImageThumbnailText } from './styles';

type Props = {
  locations: GetStructureLocationsForInspectionDataLoaderQuery['allLocations'];
  lookAt: Number3 | undefined;
  searchTerm: string;
};

export const ViewpointsTable = ({ locations, lookAt, searchTerm }: Props) => {
  const allLocations = useAtomValue(state.structureLocations);

  const [selectedSpherical, setSelectedSpherical] = useAtom(state.selectedSpherical);

  const goToViewpoint = useCallback(
    (locationId: string) => {
      const selectedViewpoint = allLocations?.find((location) => location.id === locationId);
      if (selectedViewpoint?.pose) {
        let lookAtXYZ;
        const [x, y, z] = lookAt || [];
        if (x && y && z) lookAtXYZ = { x, y, z };
        setSelectedSpherical(mapLocationToCurrentImage(selectedViewpoint, lookAtXYZ));
      }
    },
    [allLocations, lookAt, setSelectedSpherical]
  );

  const locationsForDisplay = useMemo(() => {
    return locations?.filter((location) =>
      location.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [locations, searchTerm]);

  const imageRows = useMemo(() => {
    if (locationsForDisplay) {
      return locationsForDisplay.map((location) => {
        const isSelected = location.id === selectedSpherical?.id;

        return (
          <ImageThumbailContainer onClick={() => goToViewpoint(location.id)} key={location.id}>
            <ViewpointThumbnail location={location} lookAt={lookAt} />
            <ImageThumbnailText>
              {isSelected ? (
                <Typography variant="body2" color="#fff">
                  {location.name}
                </Typography>
              ) : (
                <Link
                  underline="hover"
                  component="button"
                  variant="body2"
                  color="#fff"
                  onClick={() => goToViewpoint(location.id)}
                >
                  {location.name}
                </Link>
              )}
            </ImageThumbnailText>
          </ImageThumbailContainer>
        );
      });
    }
    return <></>;
  }, [goToViewpoint, locationsForDisplay, lookAt, selectedSpherical?.id]);

  return <DockPanel data-testid="found-viewpoints">{locations && <>{imageRows}</>}</DockPanel>;
};
